import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';

export interface SendEmailRequest {
  lead_id: string;
  subject: string;
  body: string;
  from_name?: string;
  from_email?: string;
  reply_to?: string;
  to_email?: string;
}

export interface EmailLog {
  id: string;
  lead_id: string;
  company_id: string;
  sent_by: string;
  from_email: string;
  to_email: string;
  reply_to_email: string | null;
  subject: string;
  body: string;
  resend_email_id: string | null;
  status: string;
  sent_at: string;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  error_message: string | null;
}

export const sendEmail = async (request: SendEmailRequest) => {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: request,
    });

    if (error) {
      if (error instanceof FunctionsHttpError) {
        let details: any = null;
        try {
          details = await error.context.json();
        } catch {}
        const msg = details?.error || details?.message || error.message;
        throw new Error(msg);
      }
      if (error instanceof FunctionsRelayError || error instanceof FunctionsFetchError) {
        throw new Error(error.message);
      }
      throw new Error((error as any)?.message || 'Failed to send email');
    }

    if (data && typeof data === 'object' && 'success' in data && (data as any).success === false) {
      const msg = (data as any).error || 'Failed to send email';
      throw new Error(msg);
    }

    return data;
  } catch (e) {
    if (e instanceof Error) throw e;
    throw new Error('Failed to send email');
  }
};

export const getEmailLogs = async (leadId: string): Promise<EmailLog[]> => {
  const { data, error } = await supabase
    .from('email_logs')
    .select('*')
    .eq('lead_id', leadId)
    .order('sent_at', { ascending: false });

  if (error) {
    console.error('Error fetching email logs:', error);
    throw error;
  }

  return data || [];
};

export const getCompanyEmailLogs = async (companyId: string): Promise<EmailLog[]> => {
  const { data, error } = await supabase
    .from('email_logs')
    .select('*')
    .eq('company_id', companyId)
    .order('sent_at', { ascending: false })
    .limit(100);

  if (error) {
    console.error('Error fetching company email logs:', error);
    throw error;
  }

  return data || [];
};

export const validateEmailSettings = async (companyId: string): Promise<{
  isValid: boolean;
  isVerified: boolean;
  message: string;
}> => {
  const { data: settings, error } = await supabase
    .from('company_email_settings')
    .select('is_verified, verification_status')
    .eq('company_id', companyId)
    .maybeSingle();

  if (error) {
    return {
      isValid: false,
      isVerified: false,
      message: 'Failed to fetch email settings',
    };
  }

  if (!settings) {
    return {
      isValid: false,
      isVerified: false,
      message: 'Email settings not configured. Please contact your admin.',
    };
  }

  if (!settings.is_verified) {
    return {
      isValid: false,
      isVerified: false,
      message: 'Email domain not verified. Please verify your domain in Email Settings.',
    };
  }

  return {
    isValid: true,
    isVerified: true,
    message: 'Email settings valid',
  };
};
